/**
 * Calendario vero: una griglia mensile.
 *
 * La striscia di giorni scorrevole diceva i prezzi ma non faceva vedere il
 * *mese*: dove sono i buchi, quali weekend sono ancora liberi, dove finisce
 * una prenotazione e ne comincia un'altra. Un property manager guarda il
 * calendario per capire la forma del mese, non per leggere trenta numeri in
 * fila.
 *
 * Ogni cella dice due cose e basta: il giorno e il prezzo. Le notti prenotate
 * sono piene, quelle bloccate a righe, quelle libere bianche.
 */

import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  addDays,
  eachDate,
  formatEur,
  todayInRome,
  weekday,
  type CalendarDayView,
  type IsoDate,
} from "@otto/shared";
import { cx } from "./primitives.tsx";

const GIORNI = ["lun", "mar", "mer", "gio", "ven", "sab", "dom"];

export interface MonthCalendarProps {
  days: CalendarDayView[];
  /** Mese iniziale, formato AAAA-MM. Default: quello corrente. */
  initialMonth?: string;
  onSelectRange?: (from: IsoDate, to: IsoDate) => void;
  /**
   * L'intervallo già scelto, che resta acceso finché non se ne sceglie un
   * altro. Senza, dopo il secondo tap la selezione sparirebbe proprio mentre
   * la si sta usando.
   */
  selected?: { from: IsoDate; to: IsoDate } | null;
  loading?: boolean;
  /**
   * Quale mese si sta guardando adesso (il primo dei due).
   *
   * Serve a chi ha caricato una finestra di giorni: oltre il bordo della
   * finestra il calendario non ha dati, e senza avvisare nessuno disegnava
   * caselle vuote — che si leggono come «libero a zero euro», non come «non
   * lo so».
   */
  onMeseVisibile?: (mese: string) => void;
  /** Il "oggi" del calendario. Iniettabile per i test; di norma è quello vero. */
  today?: IsoDate;
}

export function MonthCalendar({
  days,
  initialMonth,
  onSelectRange,
  selected = null,
  loading,
  onMeseVisibile,
  today = todayInRome(),
}: MonthCalendarProps) {
  const [month, setMonthGrezzo] = useState(initialMonth ?? today.slice(0, 7));
  const setMonth = (mese: string) => {
    setMonthGrezzo(mese);
    onMeseVisibile?.(mese);
  };
  const [anchor, setAnchor] = useState<IsoDate | null>(null);
  const [hover, setHover] = useState<IsoDate | null>(null);

  const byDate = useMemo(
    () => new Map(days.map((day) => [day.date, day])),
    [days],
  );

  const primo = useMemo(() => buildMonth(month), [month]);
  const secondo = useMemo(() => buildMonth(shiftMonth(month, 1)), [month]);

  const selection = useMemo(() => {
    if (anchor) {
      const other = hover ?? anchor;
      return anchor <= other
        ? { from: anchor, to: other }
        : { from: other, to: anchor };
    }
    return selected;
  }, [anchor, hover, selected]);

  /** 0 = fuori, 1 = dentro l'intervallo, 2 = uno dei due estremi. */
  const selectionRank = (date: IsoDate): 0 | 1 | 2 => {
    if (!selection || date < selection.from || date > selection.to) return 0;
    return date === selection.from || date === selection.to ? 2 : 1;
  };

  const handleClick = (date: IsoDate) => {
    if (!onSelectRange) return;
    if (!anchor) {
      setAnchor(date);
      setHover(date);
      return;
    }
    const from = anchor <= date ? anchor : date;
    const to = anchor <= date ? date : anchor;
    onSelectRange(from, to);
    setAnchor(null);
    setHover(null);
  };

  /**
   * Riepilogo del mese: un solo numero per cosa, come da §1.bis.
   *
   * Conta solo le notti da oggi in avanti. Il passato non si può più vendere,
   * e mescolarlo farebbe sembrare vuoto un mese che invece è già andato.
   */
  const summary = useMemo(() => {
    const inMese = primo.cells
      .filter((cell) => cell.inMonth)
      .map((cell) => byDate.get(cell.date))
      .filter((day): day is CalendarDayView => day != null);

    const future = inMese.filter((day) => day.date >= today);
    const visible = future.length > 0 ? future : inMese;
    const soloFuture = future.length > 0 && future.length < inMese.length;

    const booked = visible.filter((day) => day.booked).length;
    const prices = visible
      .filter((day) => !day.booked && day.priceCents != null)
      .map((d) => d.priceCents!);

    return {
      soloFuture,
      nights: visible.length,
      booked,
      occupancy: visible.length
        ? Math.round((booked / visible.length) * 100)
        : 0,
      avgFree: prices.length
        ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length)
        : null,
    };
  }, [primo, byDate, today]);

  return (
    <div className="flex flex-col gap-3">
      {/* ── Testata: le frecce ai lati, i mesi sopra le rispettive griglie ─ */}
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setMonth(primo.prev)}
          aria-label="Mese precedente"
          className="grid size-9 place-items-center rounded-field text-ink-soft transition-all duration-quick hover:bg-surface-muted hover:text-ink active:scale-95"
        >
          <ChevronLeft size={18} />
        </button>

        <div className="flex flex-1 justify-around gap-2">
          <span className="text-[15px] font-extrabold capitalize text-ink">
            {primo.label}
          </span>
          <span className="hidden text-[15px] font-extrabold capitalize text-ink lg:block">
            {secondo.label}
          </span>
        </div>

        <button
          type="button"
          onClick={() => setMonth(primo.next)}
          aria-label="Mese successivo"
          className="grid size-9 place-items-center rounded-field text-ink-soft transition-all duration-quick hover:bg-surface-muted hover:text-ink active:scale-95"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* ── Griglie: due mesi affiancati quando c'è spazio ─────────────── */}
      <div className="grid gap-x-7 gap-y-5 lg:grid-cols-2">
        <MonthGrid
          label={primo.label}
          cells={primo.cells}
          byDate={byDate}
          today={today}
          onSelectRange={onSelectRange}
          selectionRank={selectionRank}
          onPick={handleClick}
          onHover={(date) => anchor && setHover(date)}
          onLeave={() => anchor && setHover(anchor)}
        />
        <div className="hidden lg:block">
          <MonthGrid
            label={secondo.label}
            cells={secondo.cells}
            byDate={byDate}
            today={today}
            onSelectRange={onSelectRange}
            selectionRank={selectionRank}
            onPick={handleClick}
            onHover={(date) => anchor && setHover(date)}
            onLeave={() => anchor && setHover(anchor)}
          />
        </div>
      </div>

      {/* ── Legenda e riepilogo ────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 pt-1 text-[11px] font-medium text-ink-faint">
        <Legenda className="bg-surface ring-1 ring-line-strong">libero</Legenda>
        <Legenda className="bg-surface-sunken">prenotato</Legenda>
        <Legenda className="bg-warn-soft ring-1 ring-warn/25">bloccato</Legenda>
        <Legenda className="ring-1 ring-line">nessun dato</Legenda>
      </div>

      {loading ? (
        <p className="text-[12px] text-ink-faint">Carico…</p>
      ) : (
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-card bg-surface-muted/70 px-3 py-2">
          <span className="text-[13px] text-ink-soft">
            <b className="text-ink">{summary.occupancy}%</b> occupato
          </span>
          <span className="text-[13px] text-ink-soft">
            {`${summary.booked} nott${summary.booked === 1 ? "e" : "i"} su ${summary.nights}` +
              (summary.soloFuture ? " da oggi" : "")}
          </span>
          {summary.avgFree != null ? (
            <span className="text-[13px] text-ink-soft">
              media libere{" "}
              <b className="text-ink">{formatEur(summary.avgFree)}</b>
            </span>
          ) : null}
        </div>
      )}

      {onSelectRange ? (
        <p className="text-[12px] leading-relaxed text-ink-faint">
          {anchor
            ? "Tocca il giorno di fine per scegliere l’intervallo."
            : "Tocca due giorni per scegliere un intervallo e chiedere una modifica."}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Il fondo di una cella racconta lo stato della notte, e basta.
 *
 * Vive separato dal contorno perché la selezione non deve riscrivere i colori
 * del testo: un giorno scelto resta leggibile esattamente com'era prima.
 */
function fondoDellaNotte(
  day: CalendarDayView | undefined,
  rank: 0 | 1 | 2,
): string {
  // Notte di cui non sappiamo niente: resta vuota. Riempirla di grigio la
  // farebbe sembrare occupata, che è il contrario.
  if (!day) return "text-ink-faint/60";
  if (day.booked) return "bg-surface-sunken text-ink-soft";
  if (day.status === "unavailable") return "bg-warn-soft text-warn";
  // Il contorno grigio della notte libera lascia il posto a quello rosa:
  // due cornici concentriche sarebbero solo rumore.
  return rank > 0
    ? "bg-surface text-ink"
    : "bg-surface text-ink ring-1 ring-line hover:ring-line-strong";
}

/** Il contorno rosa dell'intervallo scelto: più marcato sui due estremi. */
function contornoDellaScelta(rank: 0 | 1 | 2): string {
  if (rank === 0) return "";
  return rank === 1 ? "giorno-scelto" : "giorno-estremo";
}

function Legenda({
  className,
  children,
}: {
  className: string;
  children: string;
}) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={cx("size-3 rounded", className)} />
      {children}
    </span>
  );
}

// ── Una griglia mensile ────────────────────────────────────────────────────

function MonthGrid({
  label,
  cells,
  byDate,
  today,
  onSelectRange,
  selectionRank,
  onPick,
  onHover,
  onLeave,
}: {
  label: string;
  cells: Cell[];
  byDate: Map<string, CalendarDayView>;
  today: IsoDate;
  onSelectRange?: (from: IsoDate, to: IsoDate) => void;
  selectionRank: (date: IsoDate) => 0 | 1 | 2;
  onPick: (date: IsoDate) => void;
  onHover: (date: IsoDate) => void;
  onLeave: () => void;
}) {
  return (
    <div role="group" aria-label={label}>
      <div className="mb-1.5 grid grid-cols-7 gap-1.5">
        {GIORNI.map((giorno) => (
          <div
            key={giorno}
            className="text-center text-[10px] font-bold uppercase tracking-[0.06em] text-ink-faint"
          >
            {giorno}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1.5" onMouseLeave={onLeave}>
        {cells.map((cell) => {
          const day = byDate.get(cell.date);
          const past = cell.date < today;
          const isToday = cell.date === today;
          const rank = selectionRank(cell.date);

          return (
            <button
              key={cell.date}
              type="button"
              disabled={!cell.inMonth || !onSelectRange}
              aria-pressed={onSelectRange ? rank > 0 : undefined}
              onClick={() => onPick(cell.date)}
              onMouseEnter={() => onHover(cell.date)}
              className={cx(
                "relative flex aspect-square flex-col items-center justify-center gap-0.5 rounded-card",
                "transition-all duration-quick ease-soft",
                !cell.inMonth && "invisible",
                past && "opacity-40",
                onSelectRange &&
                  cell.inMonth &&
                  "hover:-translate-y-0.5 hover:shadow-lift",
                // Il fondo dice com'è la notte; il contorno dice se è scelta.
                // Sono due cose separate: se il fondo cambiasse con la
                // selezione, il numero del giorno sparirebbe dentro il rosa.
                fondoDellaNotte(day, rank),
                contornoDellaScelta(rank),
              )}
            >
              <span
                className={cx(
                  "text-[13px] font-extrabold tabular-nums leading-none",
                  isToday && "text-accent",
                )}
              >
                {Number(cell.date.slice(8, 10))}
              </span>

              {day ? (
                <span
                  className={cx(
                    "leading-none tabular-nums",
                    day.booked
                      ? "text-[9.5px] font-bold uppercase tracking-[0.04em]"
                      : "text-[10.5px] font-semibold",
                  )}
                >
                  {day.booked
                    ? "pieno"
                    : day.priceCents != null
                      ? formatEur(day.priceCents)
                      : "—"}
                </span>
              ) : null}

              {/* Il minimo notti, solo quando è più di uno: altrimenti è rumore. */}
              {day && !day.booked && (day.minNights ?? 1) > 1 ? (
                <span
                  title={`minimo ${day.minNights} notti`}
                  className="absolute right-1.5 top-1 text-[8px] font-bold text-ink-faint"
                >
                  {day.minNights}
                </span>
              ) : null}

              {isToday ? (
                <span className="absolute bottom-1 size-1 rounded-full bg-accent" />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Costruzione del mese ────────────────────────────────────────────────────

interface Cell {
  date: IsoDate;
  inMonth: boolean;
}

function buildMonth(month: string): {
  cells: Cell[];
  label: string;
  prev: string;
  next: string;
} {
  const first = `${month}-01`;
  const year = Number(month.slice(0, 4));
  const monthNumber = Number(month.slice(5, 7));

  const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  const last = `${month}-${String(lastDay).padStart(2, "0")}`;

  // La settimana italiana comincia di lunedì: weekday() dà 0 per domenica.
  const leading = (weekday(first) + 6) % 7;
  const trailing = (7 - ((leading + lastDay) % 7)) % 7;

  const cells: Cell[] = [
    ...eachDate(addDays(first, -leading), addDays(first, -1)).map((date) => ({
      date,
      inMonth: false,
    })),
    ...eachDate(first, last).map((date) => ({ date, inMonth: true })),
    ...eachDate(addDays(last, 1), addDays(last, trailing)).map((date) => ({
      date,
      inMonth: false,
    })),
  ];

  const label = new Intl.DateTimeFormat("it-IT", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${first}T00:00:00Z`));

  return {
    cells,
    label,
    prev: shiftMonth(month, -1),
    next: shiftMonth(month, 1),
  };
}

function shiftMonth(month: string, delta: number): string {
  const year = Number(month.slice(0, 4));
  const monthNumber = Number(month.slice(5, 7));
  const total = year * 12 + (monthNumber - 1) + delta;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}`;
}
